/*
 * File: Message.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 13, 2024 at 9:21 AM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: August 4, 2025 at 3:51 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useContext, useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Utilities
import { formatNumber, formatTextRedactions, truncateString } from '../../../../utilities/utilities';
import { scrollPageToHash } from '../../../utilities/position';
import { generateShortFormContentPlaceholder } from '../../../utilities/placeholder';
import { toastError } from '../../../utilities/toaster';
import { buildDynamicMeta } from '../../../components/Meta';

// Services
import {
	fetchMessage, fetchNextMessage, fetchRandomMessage, fetchRelatedMessages
} from '../../../services/message';
import { recordReaction, removeReaction } from '../../../services/reaction';
import { recordView } from '../../../services/view';

// Store
import { updateCurrentMessage, updateCurrentMessageError } from '../../../store/slices/currentMessage/currentMessage.slice';
import { updateUser } from '../../../store/slices/user/user.slice';
import { toggleReactionAnimation } from '../../../store/slices/animation/animation.slice';

// Context
import ActionContext from '../../../context/SharedActionContext';
import AnimationContextBuilder from '../../../context/AnimationContext';

// Components
import {
	Meta,
	AppNavigation,
	Typography,
	ObfuscatedText,
	CommentCreator,
	CommentFeed,
	ContentLocalityBadge,
	LocaleLink,
	Badge,
	ActionModule,
	ToolTip,
	CardOptionsWidget,
	ErrorComponent,
	DetailPagePanel,
	Button,
	FlyToTargetContext,
	SchemaScript,
} from '../../../components';

// Constants
import {
	AVAILABLE_I18N_NAMESPACES, REACTIONS, SOURCES
} from '../../../../constants/base.constants';

// Styles
import * as S from './Message.styles';


/**
 * Component
 */

const Message = ({ meta: m }) => {

	// Get context from navigation parent
	const {
		dispatch: actionDispatch
	} = useContext(ActionContext);

	// Get context from navigation parent
	const payload = useContext(AnimationContextBuilder);
	const animationState = payload && payload.state ? payload.state : {};

	// Get component parameters
	const { slug } = useParams();

	// Get current user from hook
	const currentUser = useSelector((state) => state.user.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Create references
	const viewedMessages = useRef([]);
	const reactionActionModuleRef = useRef();

	// Get current message from hook
	let { status: messageStatus, value: message } = useSelector((state) => state.currentMessage);
	if (message?.slug && message?.slug !== slug) {
		messageStatus = 'initial';
		message = null;
	}

	// Check if loading
	const isLoading = useMemo(() => messageStatus !== 'final', [messageStatus]);

	// Create state handlers
	const [isFetching, setIsFetching] = useState(false);
	const [nextMessage, setNextMessage] = useState(null);
	const [previousMessages, setPreviousMessages] = useState([]);
	const [messageIndex, setMessageIndex] = useState(0);
	const [related, setRelated] = useState(null);
	const [pendingComments, setPendingComments] = useState([]);
	const [animationDuration, setAnimationDuration] = useState(2.6);

	// Get reaction
	const hasReaction = useMemo(() => {

		// Get reaction schema
		const reactionSchema = currentUser.reaction_schema || {};

		// Check for active reaction
		return reactionSchema[message?.message_id] && reactionSchema[message?.message_id].includes(REACTIONS.reaction_a.code);

	}, [currentUser, message]);

	// Create state
	const [isReactionActive, setIsReactionActive] = useState(hasReaction === true);

	// Use hooks
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	// Get location hash
	const { hash } = location;

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Generate placeholders
	const contentPlaceholder = useMemo(() => generateShortFormContentPlaceholder(), []);

	// Create message title
	const title = useMemo(() => {
		if (message) {
			const messageType = t(`messageCard.type.${message?.type || 'support_message'}_all`);
			return t('messageTypeNumber', { messageType, number: message?.number });
		} return '';
	}, [t, message]);

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageMessage.metaTitle', { partnerName: partner.name, messageTitle: title || t('messageName') }),
		description: truncateString(formatTextRedactions(message?.content), 160),
		bodyClasses: 'hide-captcha',
		...m || {},
		...message && message.allow_indexing === false ? {
			shouldIndex: false
		} : undefined
	}), [m, message, partner, t, title]);

	// Define parent meta content
	const parentMeta = useMemo(() => ({
		title: t('pageHome.metaTitle', { partnerName: partner.name })
	}), [partner, t]);

	// Create partner domain
	const partnerDomain = useMemo(() => {
		let domain = `https://${partner.domain}`;
		if (partner.redirect_to_subdomain) {
			domain = `https://${partner.redirect_to_subdomain}.${partner.domain}`;
		}
		return domain;
	}, [partner]);

	// Move to previous message action
	const movePrevious = () => {

		// Update next index
		setMessageIndex(messageIndex - 1);

		// Update previous messages
		setPreviousMessages(previousMessages.slice(0, -1));
	};

	// Move to next message action
	const moveNext = (q) => {

		// Update next index
		setMessageIndex(messageIndex + 1);

		// Update previous messages
		setPreviousMessages([...previousMessages, q]);
	};

	// Handle fetch current message
	const fetchCurrentMessage = async () => {
		try {

			// Update state
			setRelated(null);

			// Fetch message
			const { data: { result: { message: msg, required_redirect: requiredRedirect } } } = await fetchMessage({ slug });

			// Handle redirect if necessary
			if (requiredRedirect) {

				// Get redirect parameters
				const { path } = requiredRedirect;
				if (path) {

					// Redirect to path
					navigate(`${locale.localePath}${path}`);

					// Return
					return;
				}
			}

			// Update viewed messages
			viewedMessages.current.push(msg.message_id);

			// Update message
			dispatch(updateCurrentMessage(msg));

		} catch (e) {

			// Update state
			dispatch(updateCurrentMessageError(e.message));
		}
	};

	// Handle fetch next message
	const fetchNext = async () => {
		try {

			// Fetch next message
			const { data: { result: { message: msg } } } = await fetchNextMessage({ messageId: message.message_id, viewedMessages: viewedMessages.current });

			// Update state
			setNextMessage(msg);

		} catch (e) {

			// Update state
			setNextMessage(null);
		}
	};

	// Handle fetch random message
	const fetchRandom = async () => {

		// Update state
		setIsFetching(true);

		// Fetch random message
		try {

			// Fetch random message
			const { data: { result: { message: msg } } } = await fetchRandomMessage({ messageId: message.message_id, viewedMessages: viewedMessages.current });

			// Update state
			setIsFetching(false);

			// Update next index
			setMessageIndex(messageIndex + 1);

			// Update previous messages
			setPreviousMessages([...previousMessages, message]);

			// Navigate to new message
			navigate(`${locale.localePath}/message/${msg.slug}`);

		} catch (e) {

			// Update state
			setIsFetching(false);

			// Show error
			toastError(uiMode, partner, t('pageMessage.randomError'));
		}
	};

	// Handle fetch related messages
	const fetchRelated = async () => {
		try {

			// Fetch related messages
			const { data: { result: { messages } } } = await fetchRelatedMessages({ messageId: message.message_id });

			// Update state
			setRelated(messages || []);

		} catch (e) {

			// Update state
			setRelated([]);
		}
	};

	// Handle report message
	const reportMessage = () => {

		// Open report modal
		actionDispatch({
			type: 'OPEN_REPORT_MODAL',
			payload: {
				source: SOURCES.MESSAGE,
				sourceId: message?.message_id
			}
		});
	};

	// Handle message reaction
	const handleReaction = async () => {

		// Handle action for state
		if (!isReactionActive) {

			// Update state
			setIsReactionActive(true);

			// Trigger reaction animation
			setTimeout(() => {
				dispatch(toggleReactionAnimation(true));
				setTimeout(() => {
					dispatch(toggleReactionAnimation(false));
				}, 800);
			}, animationDuration * 1000);

			// Record reaction
			const { data: { result: { user } } } = await recordReaction({
				source: SOURCES.MESSAGE,
				entityType: SOURCES.MESSAGE,
				entityId: message?.message_id,
				code: REACTIONS.reaction_a.code,
				urlPath: window.location.pathname
			});

			// Update user
			if (user) {
				dispatch(updateUser(user));
			}

		} else {

			// Update state
			setIsReactionActive(false);

			// Remove reaction
			const { data: { result: { user } } } = await removeReaction({
				source: SOURCES.MESSAGE,
				entityType: SOURCES.MESSAGE,
				entityId: message?.message_id,
				code: REACTIONS.reaction_a.code,
				urlPath: window.location.pathname
			});

			// Update user
			if (user) {
				dispatch(updateUser(user));
			}
		}
	};

	// Handle actions on reaction state change
	useEffect(() => {

		// Update reaction active
		setIsReactionActive(hasReaction === true);

	}, [hasReaction]);

	// Handle actions on url update
	useEffect(() => {
		if (messageStatus === 'final') {

			// Scroll to hash
			scrollPageToHash(hash);
		}
	}, [messageStatus, hash]);

	// Handle actions on status update
	useEffect(() => {
		if (messageStatus === 'final') {

			// Update viewed
			viewedMessages.current = [...new Set([...viewedMessages.current, message.message_id])];

			// Record view
			recordView({
				source: SOURCES.MESSAGE,
				entityId: message.message_id,
				urlPath: window.location.pathname
			});

			// Fetch related messages
			fetchRelated();

			// Fetch next message
			fetchNext();
		}
	}, [messageStatus]);

	// Handle actions on component load
	useEffect(() => {

		// Check if should load
		if (messageStatus !== 'final') {

			// Fetch current message
			fetchCurrentMessage();
		}

	}, [slug]);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Schema.org */}
			<SchemaScript schema={{
				'@context': 'http://schema.org',
				'@type': 'BreadcrumbList',
				'@id': `${partnerDomain}/message/${message?.slug}/#BreadcrumbList`,
				itemListElement: [
					{
						'@type': 'ListItem',
						position: 1,
						item: {
							'@id': `${partnerDomain}/feed/#ListItem`,
							name: buildDynamicMeta(parentMeta.title, { partner }),
							url: `${partnerDomain}${locale.localePath}`
						}
					},
					{
						'@type': 'ListItem',
						position: 2,
						item: {
							'@id': `${partnerDomain}/message/${message?.slug}/#ListItem`,
							name: buildDynamicMeta(meta.title, { partner }),
							url: `${partnerDomain}${locale.localePath}/message/${message?.slug}`
						}
					}
				]
			}}
			/>
			<SchemaScript schema={{
				'@context': 'https://schema.org',
				'@type': 'ItemPage',
				'@id': `${partnerDomain}/message/${message?.slug}/#ItemPage`,
				name: formatTextRedactions(title),
				description: formatTextRedactions(message?.content || ''),
				url: `${partnerDomain}${locale.localePath}/message/${message?.slug}`,
				inLanguage: locale.localeBaseId,
				isPartOf: {
					'@id': `${partnerDomain}/#WebSite`
				},
				publisher: {
					'@id': `${partner.website}/#Organization`
				},
				breadcrumb: {
					'@id': `${partnerDomain}/message/${message?.slug}/#BreadcrumbList`
				},
				mainEntity: {
					'@type': ['CreativeWork', 'Message'],
					'@id': `${partnerDomain}/message/${message?.slug}/#CreativeWork`,
					name: formatTextRedactions(title),
					text: formatTextRedactions(message?.content || ''),
					url: `${partnerDomain}${locale.localePath}/message/${message?.slug}`,
					isAccessibleForFree: true
				}
			}}
			/>

			{/* Component Content */}
			<AppNavigation showFooter>
				<S.Wrapper>

					{/* Message Card */}
					{messageStatus !== 'error' ? (
						<>
							<S.MessageCard>

								{/* Card Header */}
								<S.CardHeader>

									{/* Back Button */}
									<S.BackButton icon={['far', 'arrow-left']} size={1} onClick={() => { navigate(location?.state?.back?.path || `${locale.localePath}`); }} />

									{/* Badges */}
									<S.BadgeContainer>

										{/* Exhibit Badge */}
										{message?.exhibit && (
											<LocaleLink to={`https://discover.ourwave.org/exhibit/${message?.exhibit.slug}`} target="_blank">
												<Badge type="neutral" variation="square" size="small" className="rightIcon">
													<Typography weight="medium">{message?.exhibit.short_title}</Typography>
													<FontAwesomeIcon icon={['fas', 'arrow-up-right']} />
												</Badge>
											</LocaleLink>
										)}

										{/* Story Badge */}
										{message?.story && (
											<LocaleLink to={`https://${message?.story.partner.full_domain}/story/${message?.story.link_component}`} target="_blank">
												<Badge type="neutral" variation="square" size="small" className="rightIcon">
													<Typography weight="medium">
														<Trans t={t} i18nKey="storyNumber">
															Story #
															{{ number: message?.story.number }}
														</Trans>
													</Typography>
													<FontAwesomeIcon icon={['fas', 'arrow-up-right']} />
												</Badge>
											</LocaleLink>
										)}

										{/* Partner Badge */}
										{message?.partner && partner?.id && message?.partner.id !== partner?.id && (
											<LocaleLink to={`https://${message?.partner.full_domain}`} target="_blank">
												<Badge type="neutral" variation="square" size="small" className="rightIcon">
													<Typography weight="medium">
														<Trans t={t} i18nKey="communityBadge">
															{{ partnerName: message?.partner.name }}
															{' '}
															Community
														</Trans>
													</Typography>
													<FontAwesomeIcon icon={['fas', 'arrow-up-right']} />
												</Badge>
											</LocaleLink>
										)}

									</S.BadgeContainer>

									{/* Spacer */}
									<S.Spacer />

									{/* Content Locality Badge */}
									{message && <ContentLocalityBadge country={message?.submitted_country} id={message?.message_id} />}

								</S.CardHeader>

								{/* Prompt Container */}
								<S.PromptContainer>

									{/* Message Type */}
									<Typography tag="h5" weight="bold" className={isLoading && 'shimmer shimmer-element'}>
										<Trans
											t={t}
											i18nKey={`messageCard.type.${message?.type || 'support_message'}_messages`}
										>
											Community Message
										</Trans>
									</Typography>

								</S.PromptContainer>

								{/* Trigger Warning */}
								{message?.is_triggering && message?.triggers.length > 0 && (
									<S.TriggerWarning>
										<Typography tag="p" variation="3">
											<Trans t={t} i18nKey={`triggerWarnings.${message?.triggers[0]}_message`}>
												Dear reader, the following message contains language of self-harm that some may find triggering or discomforting.
											</Trans>
										</Typography>
									</S.TriggerWarning>
								)}

								{/* Message Content */}
								<Typography tag="p" className={isLoading && 'shimmer shimmer-element'}>
									<ObfuscatedText>
										{message?.content || contentPlaceholder}
									</ObfuscatedText>
								</Typography>

								{/* Navigation Bar */}
								<S.NavigationBar>

									{/* Previous Button */}
									{previousMessages.length > messageIndex - 1 && previousMessages.length > 0 ? (
										<LocaleLink to={`/message/${previousMessages[messageIndex - 1].slug}`} onClick={movePrevious}>
											<Button disabled={isFetching} variation="secondary">
												<FontAwesomeIcon icon={['fas', 'arrow-left-long']} />
												<Typography tag="span" weight="semibold">
													<Trans t={t} i18nKey="pageMessage.previousMessage">
														Previous Message
													</Trans>
												</Typography>
											</Button>
										</LocaleLink>
									) : <div />}

									{/* Random Button */}
									{message && (
										<Button disabled={isFetching} isLoading={isFetching} onClick={fetchRandom} variation="secondary">
											<Typography tag="span" weight="semibold">
												<Trans t={t} i18nKey="pageMessage.randomMessage">
													Random Message
												</Trans>
											</Typography>
											<FontAwesomeIcon icon={['fas', 'shuffle']} />
										</Button>
									)}

									{/* Next Button */}
									{nextMessage ? (
										<LocaleLink to={`/message/${nextMessage.slug}`} onClick={() => { moveNext(message); }}>
											<Button disabled={isFetching} variation="secondary">
												<Typography tag="span" weight="semibold">
													<Trans t={t} i18nKey="pageMessage.nextMessage">
														Next Message
													</Trans>
												</Typography>
												<FontAwesomeIcon icon={['fas', 'arrow-right-long']} />
											</Button>
										</LocaleLink>
									) : <div />}

								</S.NavigationBar>

								{/* Action Bar */}
								<S.ActionBar>

									{/* Action - React */}
									<FlyToTargetContext
										targets={{ desktop: 'desktopReactionMetric', mobile: 'mobileReactionMetric' }}
										flyElement={<FontAwesomeIcon className="love active" icon={['fas', 'heart']} />}
										navigationState={animationState}
										ref={reactionActionModuleRef}
										refOffsetTop={-5}
										setAnimationDuration={setAnimationDuration}
									>
										<ActionModule
											onClick={handleReaction}
											canFly={!isReactionActive}
											data-tip={t('reactions.reaction_a')}
											data-for={`${message?.message_id}-heart`}
											ref={reactionActionModuleRef}
										>
											<FontAwesomeIcon className={isReactionActive ? 'love active' : 'love'} icon={isReactionActive ? ['fas', 'heart'] : ['far', 'heart']} />
											{isReactionActive && (
												<Typography tag="span">
													<Trans t={t} i18nKey="youAndOthers">
														<Typography className="fontBold">you</Typography>
														{' '}
														<Typography className="fontRegular">and</Typography>
														{' '}
														<Typography className="fontBold">others</Typography>
													</Trans>
												</Typography>
											)}
										</ActionModule>
									</FlyToTargetContext>

									{/* Action - Comment */}
									{message?.comments_allowed && (
										<LocaleLink to={`${location.pathname}#comments`} disableLocale>
											<ActionModule>
												<FontAwesomeIcon className="comment" icon={['far', 'comment']} />
												<Typography tag="span">{formatNumber(message?.comment_count) || '–'}</Typography>
											</ActionModule>
										</LocaleLink>
									)}

									{/* Tooltip */}
									<ToolTip id={`${message?.message_id}-heart`} weight="medium" />

									{/* Spacer */}
									<S.ActionSpacer />

									{/* Options Dropdown */}
									<CardOptionsWidget items={[
										{
											icon: ['far', 'flag'],
											text: t('actions.report'),
											action: reportMessage
										}]}
									/>
								</S.ActionBar>

								{/* Comments Container */}
								<S.CommentsContainer>

									{/* Comment Signpost */}
									<S.CommentSignpost id="comments" />

									{/* Show comments if necessary */}
									{message?.comments_allowed ? (
										<>
											{/* Comment Creator */}
											<CommentCreator source={SOURCES.MESSAGE} sourceId={message?.message_id} postCreateActions={(commentObj) => { setPendingComments([...pendingComments, commentObj]); }} />

											{/* Comment Feed */}
											<CommentFeed source={SOURCES.MESSAGE} sourceId={message?.message_id} pendingComments={pendingComments} />
										</>
									) : (
										<S.CommentsDisabledLabel>
											<Typography tag="p" weight="medium" variation="2" center>
												<Trans t={t} i18nKey="pageMessage.commentsDisabled">
													Replies are disabled for this message
												</Trans>
											</Typography>
										</S.CommentsDisabledLabel>
									)}

								</S.CommentsContainer>

							</S.MessageCard>

							{/* Detail Page Panel */}
							<DetailPagePanel
								source={SOURCES.MESSAGE}
								sourceId={message?.message_id}
								related={related}
								onRelatedClick={() => { moveNext(message); }}
								className="detailPanel"
							/>
						</>
					) : <ErrorComponent showMeta />}
				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

Message.propTypes = {
	meta: PropTypes.shape()
};
Message.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default Message;
